import React, { useEffect, useState } from 'react';
import { MdCreditCard } from 'react-icons/md';
import API from '../api';

const fmt = n => Number(n).toLocaleString('en-US');
const STATE_LABEL = {
    complimentary: ['Complimentary', '#e2e8f0', '#334155'],
    trialing: ['Trial', '#dbeafe', '#1d4ed8'],
    active: ['Active', '#dcfce7', '#15803d'],
    grace: ['Overdue (grace)', '#fef3c7', '#b45309'],
    read_only: ['Read-only', '#fee2e2', '#b91c1c'],
};

// Developer page: see every organization's subscription, record offline payments,
// give free access, and edit plan prices. English only (platform-owner tool).
export default function BillingAdminCard() {
    const [data, setData] = useState(null);
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState('');
    const [edits, setEdits] = useState({});
    const [rules, setRules] = useState(null);
    const emptyPlan = { code: '', name: '', monthly_rwf: '', yearly_rwf: '', max_users: '', features: [] };
    const [newPlan, setNewPlan] = useState(emptyPlan);

    const load = () => API.get('/billing/dev/overview').then(({ data: d }) => { setData(d); setEdits({}); setRules(d.settings); })
        .catch(err => setMessage(err.response?.data?.error || 'Could not load billing.'));
    useEffect(() => { load(); }, []);

    const act = async (dbName, body, confirmText) => {
        if (confirmText && !window.confirm(confirmText)) return;
        setBusy(dbName); setMessage('');
        try { await API.post('/billing/dev/subscription', { db_name: dbName, ...body }); await load(); }
        catch (err) { setMessage(err.response?.data?.error || 'Action failed.'); }
        finally { setBusy(''); }
    };

    const saveRules = async () => {
        setBusy('rules'); setMessage('');
        try {
            await API.put('/billing/dev/settings', rules);
            await load();
            setMessage('Trial and grace settings saved.');
        } catch (err) { setMessage(err.response?.data?.error || 'Could not save the settings.'); }
        finally { setBusy(''); }
    };

    const addPlan = async () => {
        setBusy('new-plan'); setMessage('');
        try {
            await API.post('/billing/dev/plans', newPlan);
            setNewPlan(emptyPlan);
            await load();
            setMessage('Plan added.');
        } catch (err) { setMessage(err.response?.data?.error || 'Could not add the plan.'); }
        finally { setBusy(''); }
    };

    const savePlan = async plan => {
        const e = edits[plan.code] || {};
        setBusy(plan.code); setMessage('');
        try {
            await API.put(`/billing/dev/plans/${plan.code}`, {
                name: e.name ?? plan.name, monthly_rwf: e.monthly_rwf ?? plan.monthly_rwf,
                yearly_rwf: e.yearly_rwf ?? plan.yearly_rwf, max_users: e.max_users ?? (plan.max_users || ''),
                active: e.active ?? plan.active, features: e.features ?? plan.features,
            });
            await load();
            setMessage(`Saved ${plan.code}.`);
        } catch (err) { setMessage(err.response?.data?.error || 'Could not save the plan.'); }
        finally { setBusy(''); }
    };

    if (!data) return message ? <div className="alert alert-danger">{message}</div> : null;
    if (data.billing_enabled === false) return null;  // subscriptions are switched off for now
    const setEdit = (code, key, value) => setEdits(cur => ({ ...cur, [code]: { ...(cur[code] || {}), [key]: value } }));
    const cell = { padding: '6px 8px', verticalAlign: 'middle' };
    const catalog = data.feature_catalog || [];
    const toggle = (list, key) => (list.includes(key) ? list.filter(k => k !== key) : [...list, key]);
    const FeatureBoxes = ({ value, onChange }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 200 }}>
            {catalog.map(feature => (
                <label key={feature.key} style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
                    <input type="checkbox" checked={value.includes(feature.key)} onChange={() => onChange(toggle(value, feature.key))} />
                    {feature.label}
                </label>
            ))}
        </div>
    );

    return (
        <div className="page-card" style={{ padding: 18, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <MdCreditCard style={{ color: '#0b5ed7' }} />
                <h6 style={{ margin: 0, fontWeight: 700 }}>Subscriptions &amp; billing</h6>
            </div>
            <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 10px' }}>
                Online payments are <strong>{data.payments_enabled
                    ? `ON (${(data.payment_providers || []).map(name => ({ flutterwave: 'Flutterwave', mtn: 'MTN MoMo', airtel: 'Airtel Money', paypack: 'Paypack' }[name] || name)).join(', ')} connected)`
                    : 'OFF (add the MTN MoMo or Airtel Money keys on the server)'}</strong>.
                Systems marked Complimentary are never limited.
            </p>
            {message && <div className="alert alert-info py-2" style={{ fontSize: 13 }}>{message}</div>}

            <div style={{ overflowX: 'auto' }}>
                <table className="table mb-3" style={{ fontSize: 13 }}>
                    <thead><tr><th>Organization</th><th>Plan</th><th>Status</th><th>Until</th><th>Actions</th></tr></thead>
                    <tbody>
                        {data.systems.map(system => {
                            const s = system.subscription;
                            const [label, bg, color] = STATE_LABEL[s.state] || STATE_LABEL.active;
                            const disabled = busy === system.db_name;
                            return (
                                <tr key={system.db_name}>
                                    <td style={cell}><strong>{system.name}</strong><div style={{ color: '#64748b', fontSize: 11.5 }}>{system.slug}</div></td>
                                    <td style={cell}>{s.plan_name || '—'}</td>
                                    <td style={cell}><span style={{ background: bg, color, fontWeight: 700, fontSize: 11.5, padding: '2px 8px', borderRadius: 999 }}>{label}</span></td>
                                    <td style={cell}>{s.ends_at ? new Date(s.ends_at).toLocaleDateString() : '—'}</td>
                                    <td style={cell}>
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            <button className="btn btn-sm btn-outline-primary" disabled={disabled}
                                                onClick={() => act(system.db_name, { action: 'extend', days: 30 }, `Record a paid month for ${system.name}?`)}>+30 days</button>
                                            <button className="btn btn-sm btn-outline-primary" disabled={disabled}
                                                onClick={() => act(system.db_name, { action: 'extend', days: 365 }, `Record a paid year for ${system.name}?`)}>+1 year</button>
                                            <button className="btn btn-sm btn-outline-secondary" disabled={disabled}
                                                onClick={() => act(system.db_name, { action: 'trial' }, `Restart the free trial for ${system.name}?`)}>Restart trial</button>
                                            <button className="btn btn-sm btn-outline-secondary" disabled={disabled}
                                                onClick={() => act(system.db_name, { action: 'complimentary' }, `Give ${system.name} free, unlimited access?`)}>Make free</button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {rules && (
                <>
                    <h6 style={{ fontWeight: 700 }}>Trial &amp; grace period</h6>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 18 }}>
                        <label style={{ fontSize: 12.5 }}>Free trial (days)
                            <input className="form-control form-control-sm" type="number" min="0" max="365" style={{ width: 120 }}
                                value={rules.trial_days} onChange={ev => setRules({ ...rules, trial_days: ev.target.value })} />
                        </label>
                        <label style={{ fontSize: 12.5 }}>Grace after expiry (days)
                            <input className="form-control form-control-sm" type="number" min="0" max="90" style={{ width: 120 }}
                                value={rules.grace_days} onChange={ev => setRules({ ...rules, grace_days: ev.target.value })} />
                        </label>
                        <label style={{ fontSize: 12.5 }}>User limit during trial follows
                            <select className="form-select form-select-sm" style={{ width: 160 }}
                                value={rules.trial_plan} onChange={ev => setRules({ ...rules, trial_plan: ev.target.value })}>
                                {data.plans.map(plan => <option key={plan.code} value={plan.code}>{plan.name}</option>)}
                            </select>
                        </label>
                        <button className="btn btn-sm btn-primary" disabled={busy === 'rules'} onClick={saveRules}>Save</button>
                    </div>
                    <div style={{ color: '#64748b', fontSize: 12, margin: '-10px 0 16px' }}>Applies to new sign-ups; existing trials keep their end date.</div>
                </>
            )}

            <h6 style={{ fontWeight: 700 }}>Plans &amp; prices (RWF)</h6>
            <div style={{ overflowX: 'auto' }}>
                <table className="table mb-2" style={{ fontSize: 13 }}>
                    <thead><tr><th>Code</th><th>Name</th><th>Monthly</th><th>Yearly</th><th>Max users (blank = unlimited)</th><th>Services included</th><th>On sale</th><th /></tr></thead>
                    <tbody>
                        {data.plans.map(plan => {
                            const e = edits[plan.code] || {};
                            return (
                                <tr key={plan.code}>
                                    <td style={cell}>{plan.code}</td>
                                    <td style={cell}><input className="form-control form-control-sm" value={e.name ?? plan.name} onChange={ev => setEdit(plan.code, 'name', ev.target.value)} /></td>
                                    <td style={cell}><input className="form-control form-control-sm" type="number" min="0" value={e.monthly_rwf ?? plan.monthly_rwf} onChange={ev => setEdit(plan.code, 'monthly_rwf', ev.target.value)} /></td>
                                    <td style={cell}><input className="form-control form-control-sm" type="number" min="0" value={e.yearly_rwf ?? plan.yearly_rwf} onChange={ev => setEdit(plan.code, 'yearly_rwf', ev.target.value)} /></td>
                                    <td style={cell}><input className="form-control form-control-sm" type="number" min="1" value={e.max_users ?? (plan.max_users || '')} onChange={ev => setEdit(plan.code, 'max_users', ev.target.value)} /></td>
                                    <td style={cell}><FeatureBoxes value={e.features ?? plan.features} onChange={list => setEdit(plan.code, 'features', list)} /></td>
                                    <td style={cell}><input type="checkbox" checked={e.active ?? plan.active} onChange={ev => setEdit(plan.code, 'active', ev.target.checked)} /></td>
                                    <td style={cell}><button className="btn btn-sm btn-primary" disabled={busy === plan.code || !edits[plan.code]} onClick={() => savePlan(plan)}>Save</button></td>
                                </tr>
                            );
                        })}
                        <tr>
                            <td style={cell}><input className="form-control form-control-sm" placeholder="code" value={newPlan.code} onChange={ev => setNewPlan({ ...newPlan, code: ev.target.value })} /></td>
                            <td style={cell}><input className="form-control form-control-sm" placeholder="Name" value={newPlan.name} onChange={ev => setNewPlan({ ...newPlan, name: ev.target.value })} /></td>
                            <td style={cell}><input className="form-control form-control-sm" type="number" min="0" placeholder="Monthly" value={newPlan.monthly_rwf} onChange={ev => setNewPlan({ ...newPlan, monthly_rwf: ev.target.value })} /></td>
                            <td style={cell}><input className="form-control form-control-sm" type="number" min="0" placeholder="Yearly" value={newPlan.yearly_rwf} onChange={ev => setNewPlan({ ...newPlan, yearly_rwf: ev.target.value })} /></td>
                            <td style={cell}><input className="form-control form-control-sm" type="number" min="1" placeholder="Unlimited" value={newPlan.max_users} onChange={ev => setNewPlan({ ...newPlan, max_users: ev.target.value })} /></td>
                            <td style={cell}><FeatureBoxes value={newPlan.features} onChange={list => setNewPlan({ ...newPlan, features: list })} /></td>
                            <td style={cell} />
                            <td style={cell}><button className="btn btn-sm btn-outline-primary" disabled={busy === 'new-plan'} onClick={addPlan}>Add plan</button></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div style={{ color: '#64748b', fontSize: 12 }}>Recent payments: {data.recent_payments.length === 0 ? 'none yet' :
                data.recent_payments.slice(0, 5).map(p => `${fmt(p.amount)} ${p.currency} (${p.plan_code})`).join(' · ')}</div>
        </div>
    );
}
